import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Input, Label } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';

interface ForgotPasswordPageProps {
  siteName: string;
}

// V2 reset_password_northgate_park (step 1 — request the link).
//
// Always shows the same confirmation copy after submit (whether the email
// exists or not) — the API also returns 200 in both cases by design.
export function ForgotPasswordPage({ siteName }: ForgotPasswordPageProps) {
  const displayName = siteName?.trim() || 'Access Portal';
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.post('/api/v1/auth/forgot', { email });
      setSent(true);
    } catch (err) {
      // Rate-limited / malformed input — surface the message; the API
      // never reveals account existence here.
      const msg = err instanceof ApiError ? err.message : 'Unable to request reset';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="bg-background min-h-screen flex flex-col items-center justify-center p-margin-mobile md:p-margin-desktop">
      <section className="relative z-10 w-full max-w-[440px] bg-surface-container-lowest p-8 md:p-10 rounded-xl shadow-sm border border-outline-variant">
        {/* GAP-AF2 (audit 2026-05-21): site identity block parallels
            SignInPage so /forgot-password doesn't read as a generic
            page. Subtitle below carries the step intent. */}
        <header className="text-center mb-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary mb-4">
            <Icon name="business" className="text-3xl" />
          </div>
          <h1 className="font-headline-md text-headline-md text-on-surface mb-1">
            {displayName}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mb-6">
            Reset your password
          </p>
        </header>
        <p className="font-body-md text-body-md text-on-surface-variant text-center mb-8">
          {sent
            ? `If an account exists for that address at ${displayName}, a reset link is on its way.`
            : 'Enter your email and we will send a one-time reset link.'}
        </p>

        {!sent ? (
          <form className="space-y-6" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label
                htmlFor="forgot-email"
                className="font-label-md text-label-md text-on-surface block px-1"
              >
                Email Address
              </Label>
              <div className="relative group">
                <Icon
                  name="mail"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary transition-colors"
                />
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  placeholder="name@example.com"
                  className="pl-10"
                />
              </div>
            </div>

            {error ? (
              <p role="alert" className="font-body-md text-body-md text-error">
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" loading={submitting}>
              Send reset link
            </Button>
          </form>
        ) : (
          <Link
            to="/sign-in"
            className="block w-full text-center py-3.5 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all"
          >
            Back to sign in
          </Link>
        )}

        <footer className="mt-10 pt-6 border-t border-outline-variant text-center">
          <Link
            to="/sign-in"
            className="font-label-md text-label-md text-primary hover:underline"
          >
            Return to sign in
          </Link>
        </footer>
      </section>
    </main>
  );
}
